import { getRequestInstance } from "api/services/utils";
import { RequestTypes } from "api/services/enums";
import {
  PwmProfileExchangeGetByIdDocument,
  PwmProfileExchangeListPaginatedDocument,
  PwmProfileExchangeCreateDocument,
  PwmProfileExchangeUpdateDocument,
  PwmProfileExchangeDeleteDocument,
} from "api/generated";
import { Optional } from "config";
import { executeEviction } from "api/utils/cache-eviction";
import { client } from "config/apollo";

import {
  IPwmProfileExchangeCreateVars,
  IPwmProfileExchangeModel,
  IPwmProfileExchangePaginatedResponse,
  IPwmProfileExchangePaginatedVars,
  IPwmProfileExchangeUpdateVars,
} from "./types";

export async function getPwmProfileExchange(
  id: number
): Promise<Optional<IPwmProfileExchangeModel>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: PwmProfileExchangeGetByIdDocument,
      variables: {
        id,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.pwmProfileExchangeGetById;
  } catch (error) {
    throw error;
  }
}

export async function getPwmProfileExchangePaginated(
  variables: IPwmProfileExchangePaginatedVars
): Promise<Optional<IPwmProfileExchangePaginatedResponse>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: PwmProfileExchangeListPaginatedDocument,
      variables,
      fetchPolicy: "network-only",
    });

    return res?.data?.pwmProfileExchangeListPaginated;
  } catch (error) {
    throw error;
  }
}

export async function createPwmProfileExchange(
  variables: IPwmProfileExchangeCreateVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: PwmProfileExchangeCreateDocument,
      variables,
      fetchPolicy: "network-only",
    });

    // Evict own lists + parent PWM profile entity
    executeEviction(client, {
      typename: "PwmProfileExchange",
      fieldNames: ["pwmProfileExchangeListPaginated"],
    });

    executeEviction(client, {
      typename: "PwmProfile",
      id: variables.profileId.toString(),
      fieldNames: ["pwmProfileGetById"],
    });

    return Boolean(res?.data?.pwmProfileExchangeCreate);
  } catch (error) {
    throw error;
  }
}

export async function updatePwmProfileExchange(
  variables: IPwmProfileExchangeUpdateVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: PwmProfileExchangeUpdateDocument,
      variables,
      fetchPolicy: "network-only",
    });

    // Evict updated entity + own lists + getById
    executeEviction(client, {
      typename: "PwmProfileExchange",
      id: variables.id.toString(),
      fieldNames: [
        "pwmProfileExchangeListPaginated",
        "pwmProfileExchangeGetById",
      ],
    });

    // Evict all parent PWM profile entities (profileId not available)
    executeEviction(client, {
      typename: "PwmProfile",
      fieldNames: ["pwmProfileGetById"],
    });

    return Boolean(res?.data?.pwmProfileExchangeUpdate);
  } catch (error) {
    throw error;
  }
}

export async function deletePwmProfileExchange(id: number): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: PwmProfileExchangeDeleteDocument,
      variables: {
        id,
      },
      fetchPolicy: "no-cache",
    });

    // Evict deleted entity + own lists
    executeEviction(client, {
      typename: "PwmProfileExchange",
      id: id.toString(),
      fieldNames: ["pwmProfileExchangeListPaginated"],
    });

    // Evict all parent PWM profile entities (profileId not available)
    executeEviction(client, {
      typename: "PwmProfile",
      fieldNames: ["pwmProfileGetById"],
    });

    return Boolean(res?.data?.pwmProfileExchangeDelete);
  } catch (error) {
    throw error;
  }
}
